import { assignUids, stripUids } from "./uid-generator.js";
import constants from "@supplement-constants/constants.json";

function normalizeColor(color) {
  if (!color) return color;
  const trimmed = color.trim();
  if (!trimmed) return trimmed;
  // Add # prefix if it looks like a hex color without one
  if (/^[0-9a-fA-F]{3,8}$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  return trimmed;
}

// Static options derived from shared constants (baked in at build time).
// Order comes from the explicit dietary_ingredient_order array, not from
// object key order (which is not guaranteed in JSON).
const DIETARY_NUTRIENT_OPTIONS = constants.dietary_ingredient_order.map((key, index) => {
  const meta = constants.dietary_nutrient_metadata[key];
  return {
    value: key,
    label: meta.name,
    unit: meta.unit,
    indent_level: meta.indent_level,
    order: index,
  };
});

const AGE_CATEGORY_OPTIONS = Object.entries(constants.age_categories)
  .sort(([a], [b]) => constants.age_category_order[a] - constants.age_category_order[b])
  .map(([value, label]) => ({ value, label }));

const BLEND_DISPLAY_MODE_OPTIONS = Object.entries(constants.blend_display_modes).map(
  ([value, label]) => ({ value, label })
);

const SUB_NUTRIENT_PARENTS = constants.sub_nutrient_parents;

export const Transformers = {
  _assignUids(lines, uidGenerator) {
    return assignUids(lines, uidGenerator);
  },

  _initializeDietaryLines(serverData, dietaryNutrientOptions) {
    const serverMap = {};
    (serverData || []).forEach((line) => {
      serverMap[line.dv_key] = line;
    });

    // Use backend dietary nutrient options to determine available keys and defaults
    const options = dietaryNutrientOptions || [];
    return options.map((opt) => ({
      dv_key: opt.value,
      amount: serverMap[opt.value]?.amount || "",
      unit: serverMap[opt.value]?.unit || opt.unit,
      italicize_parenthetical: serverMap[opt.value]?.italicize_parenthetical || false,
      footnote_text: serverMap[opt.value]?.footnote_text || "",
      custom_footnote_char: serverMap[opt.value]?.custom_footnote_char || "",
      footnote_on_amount: serverMap[opt.value]?.footnote_on_amount || false,
    }));
  },

  _stripUids(lines) {
    return stripUids(lines);
  },

  blendsFromServer(blends, uidGenerator) {
    // Always generate fresh UIDs on load (like lines do via _assignUids).
    // Server UIDs are ignored to prevent type mismatch collisions
    // (server returns strings like "2", counter generates numbers like 2).
    return (blends || []).map((b) => ({
      _uid: uidGenerator(),
      name: b.name,
      amount: b.amount,
      unit: b.unit,
      display_mode: b.display_mode || "inline",
      italicize_parenthetical: b.italicize_parenthetical,
      hide_dagger: b.hide_dagger,
      source: b.source,
      extra_text: b.extra_text,
      footnote_text: b.footnote_text,
      custom_footnote_char: b.custom_footnote_char,
      footnote_on_amount: b.footnote_on_amount,
      constituents: (b.constituents || []).map((c) => {
        // eslint-disable-next-line no-unused-vars
        const { _uid, ...rest } = c;
        return { ...rest, _uid: uidGenerator() };
      }),
    }));
  },

  blendsForServer(blends) {
    // Strip UIDs before sending (like lines do via _stripUids).
    // UIDs are frontend-only for Vue rendering, not persisted.
    if (!blends || !Array.isArray(blends)) return [];
    return blends.map((b) => {
      // eslint-disable-next-line no-unused-vars
      const { _uid, ...rest } = b;
      return {
        ...rest,
        display_mode: b.display_mode || "inline",
        constituents: (b.constituents || []).map((c) => {
          // eslint-disable-next-line no-unused-vars
          const { _uid, ...cRest } = c;
          return cRest;
        }),
      };
    });
  },

  labelFromServer(data, uidGenerator) {
    return {
      id: data.id,
      userId: data.user_id,
      lockVersion: data.lock_version || 0,
      serverLockVersion: data.lock_version || 0,
      isConflicted: false,
      name: data.name || "",
      servingSizeText: data.serving_size_text || "",
      servingsPerContainer: data.servings_per_container || "",
      amountPerText: data.amount_per_text || "Serving",
      singleRowLayout: data.single_row_layout || false,
      hideServingsPerContainer: data.hide_servings_per_container || false,
      ageCategory: data.age_category || "adult",
      displayAgeCategories: data.display_age_categories || [],
      format: data.format || "fda/supplement_adult/vertical",

      dvLines: this._assignUids(data.dv_lines, uidGenerator),
      linesWithoutDv: this._assignUids(data.lines_without_dv, uidGenerator),
      dietaryLines: this._initializeDietaryLines(data.dietary_lines, DIETARY_NUTRIENT_OPTIONS),
      blends: this.blendsFromServer(data.blends || [], uidGenerator),

      otherIngredients: data.other_ingredients || "",
      allergens: data.allergens || "",
      facilityAllergens: data.facility_allergens || "",
      businessInfo: data.business_info || "",
      boldOptions: data.bold_options || {},
      defaultAllergens: data.default_allergens || "",
      defaultFacilityAllergens: data.default_facility_allergens || "",
      defaultFacilityAllergensPrefix: data.default_facility_allergens_prefix || "",

      hideName: data.hide_name,
      hideFacts: data.hide_facts,
      hideOtherIngredients: data.hide_other_ingredients,
      hideAllergens: data.hide_allergens,
      hideFacilityAllergens: data.hide_facility_allergens,
      hideBusinessInfo: data.hide_business_info,
      hideLogo: data.hide_logo,
      hideBarcode: data.hide_barcode,

      textColor: data.text_color,
      backgroundColor: data.background_color,
      transparentBackground: data.background_color === "",
      textAlign: data.text_align,
      textTransform: data.text_transform,
      useLargerFootnotes: data.use_larger_footnotes || false,

      labelWidth: data.label_width || 227,
      logoImage: data.logo,
      logoScale: parseFloat(data.logo_scale || 1.0),
      logoMargin: parseFloat(data.logo_margin || 8),
      primaryBarcode: data.primary_barcode || null,
      bioengineered: data.bioengineered || false,
      proteinScore: data.protein_score != null ? parseFloat(data.protein_score) : null,

      isPublic: data.public || false,
      watermark: data.watermark || false,

      lastImportedRecipe: data.last_imported_recipe || null,

      dietaryNutrientOptions: DIETARY_NUTRIENT_OPTIONS,
      fdaNutrientOptions: data.fda_nutrient_options || [],
      formatOptions: data.format_options || [],
      ageCategoryOptions: AGE_CATEGORY_OPTIONS,
      blendDisplayModeOptions: BLEND_DISPLAY_MODE_OPTIONS,
      amountPerTextOptions: data.amount_per_text_options || [],
      subNutrientParents: SUB_NUTRIENT_PARENTS,
      validationWarnings: data.validation_warnings || {},

      // Handle calculated values if present
      ...(data.calculated_values ? { calculatedValues: data.calculated_values } : {}),

      // FDA rounding rules (single source of truth from server)
      ...(data.rounding_rules ? { roundingRules: data.rounding_rules } : {}),
    };
  },

  labelForServer(state) {
    return {
      lock_version: state.lockVersion,
      name: state.name,
      serving_size_text: state.servingSizeText,
      servings_per_container: state.servingsPerContainer,
      amount_per_text: state.amountPerText,
      single_row_layout: state.singleRowLayout,
      age_category: state.ageCategory,
      display_age_categories: state.displayAgeCategories,
      format: state.format,
      dv_lines: this._stripUids(state.dvLines),
      lines_without_dv: this._stripUids(state.linesWithoutDv),
      dietary_lines: state.dietaryLines.filter((l) => l.amount),
      blends: this.blendsForServer(state.blends),
      other_ingredients: state.otherIngredients,
      allergens: state.allergens,
      facility_allergens: state.facilityAllergens,
      business_info: state.businessInfo,
      bold_options: state.boldOptions,
      text_color: normalizeColor(state.textColor),
      background_color: state.transparentBackground ? "" : normalizeColor(state.backgroundColor),
      text_align: state.textAlign,
      text_transform: state.textTransform,
      bioengineered: state.bioengineered,
      protein_score: state.proteinScore,
      label_width: state.labelWidth,
      hide_logo: state.hideLogo,
      logo_scale: state.logoScale,
      logo_margin: state.logoMargin,
      hide_barcode: state.hideBarcode,
      hide_name: state.hideName,
      hide_facts: state.hideFacts,
      hide_other_ingredients: state.hideOtherIngredients,
      hide_allergens: state.hideAllergens,
      hide_facility_allergens: state.hideFacilityAllergens,
      hide_business_info: state.hideBusinessInfo,
      use_larger_footnotes: state.useLargerFootnotes,
      hide_servings_per_container: state.hideServingsPerContainer,
    };
  },
};
